/**
 * Sprint 111 — El producto capturado "con un peso mientras le pongo el precio".
 *
 * ## De dónde sale
 *
 * El 2026-08-26 el negocio tuvo que recapturar unos veinte productos y el dueño decidió, con toda
 * razón práctica: *"seamos prácticos, le ponemos 1 peso de precio y le digo a Claudia que los
 * revise"*. Capturar veinte precios de memoria a las tres de la mañana habría sido peor.
 *
 * El riesgo no está en el punto de venta —ahí el precio se ve antes de cobrar— sino en **el enlace
 * público**: un cliente abre "Arma tu pedido", ve *Picaña 1 kg — $1.00*, pide seis, y ese pedido
 * entra con folio y como legítimo. Nadie lo revisó porque no había nada que revisar.
 *
 * ## La regla
 *
 * Un precio de venta que no llega a **dos pesos** no es un precio: es un recordatorio de que falta
 * ponerlo. Ningún producto real de un asadero cuesta un peso, y si algún día lo hubiera, se
 * captura en 2 y se documenta — perder ese caso improbable vale mucho menos que regalar una picaña.
 *
 * ## Dónde se hace cumplir
 *
 * En **las dos puertas del track público**, no en una: al armar el menú y al cotizar el pedido que
 * manda el navegador. Un filtro que sólo vive en la pantalla es exactamente cómo cinco paquetes
 * del cliente quedaron invendibles en el Sprint 109, y cómo se capturaron refrescos que no se
 * podían vender.
 *
 * No se toca el POS a propósito: ahí el operador **sí** debe poder ver y vender lo que capturó —
 * es su catálogo y él sabe lo que está haciendo. Lo que no puede es que un desconocido lo compre.
 */

/** Sprint 39 — Todo el dinero del sistema vive en centavos. Dos pesos. */
export const PRECIO_PENDIENTE_MAX_CENTAVOS = 200;

/**
 * ¿Este precio es un "ahorita se lo pongo" en vez de un precio de venta?
 *
 * El cero también entra: un producto sin precio tampoco se le puede ofrecer a nadie.
 */
export function precioEstaPendiente(precioCentavos: number): boolean {
  if (!Number.isFinite(precioCentavos)) return true;
  return precioCentavos < PRECIO_PENDIENTE_MAX_CENTAVOS;
}

/** Lo que ve el operador en su catálogo, en su idioma y sin regañarlo. */
export const ETIQUETA_PRECIO_PENDIENTE = 'Falta precio';

/**
 * Lo que se le dice al cliente si alcanza a meter uno al carrito antes de que el menú se refresque.
 *
 * No menciona el precio: decirle *"ese producto cuesta un peso y no te lo vamos a vender"* invita
 * a discutir. Se le dice lo mismo que si se hubiera agotado, que para él es la misma situación.
 */
export const MENSAJE_PRECIO_PENDIENTE =
  'Algo de tu pedido ya no está disponible. Vuelve a armarlo.';
